import { useCostStore } from './cost';
import { useScheduleStore } from './schedule';
import { useTransactStore } from './transact'
import { ref, computed, reactive, watchEffect, watch, nextTick } from 'vue'
import { defineStore } from 'pinia'
import {
  ACCOUNT_SIDE,
  ACCOUNT_TYPE,
  PAY_ACCOUNT,
  PAY_CYCLE,
  DAY_TYPE,
  LEAVE_TYPE,
  HOLIDAY_TYPE,
  PERIOD_STATUS,
  type Payslip,
  type DailyTime,
  type Transact,
  type Employee,
  type Period,
  type Account,
  type Schedule,
  type CostCenter,
  SALARY_SCHEME,
  SUPERVISOR_ALLOWANCE_CALC,
} from './types'
import { useEmployeeStore } from './employee'
import { useAppStore } from './app'
import { useAccountStore } from '@/stores/account'
import { usePeriodStore } from '@/stores/period'
import { useTableStore } from '@/stores/table'
import { useDailyTimeStore } from '@/stores/dailytime'
import { useLoanStore } from '@/stores/loan'
import { useSettingsStore } from '@/stores/settings'

import { useUtil } from '@/composables/util'
import axios from 'axios'
import dayjs from 'dayjs'


const apiURL: string = import.meta.env.VITE_API_URL + '/api'
// const apiURL = "http://localhost:4000/api"

const api = axios.create({
  baseURL: apiURL + '/v1'
})

export const usePayslipStore = defineStore('payslip', () => {
  // Payslip Header
  const payslips = reactive<Payslip[]>([])
  const selectedPayslip = ref({} as Payslip) // selected in table
  const thisPayslip = ref({} as Payslip) //for form editing
  const thisLogHours = ref([] as any) // for this payslip


  // const dirtyPayslips = reactive<string[]>([]) // dirtyPayslips for recalculation

  const showForm = ref(false)

  // uuid: string
  // Payslip_number: string
  // Payslip_side: Payslip_SIDE
  // Payslip_name: string
  // Payslip_type: Payslip_TYPE
  const employeeStore = useEmployeeStore()
  const transactStore = useTransactStore()
  const appStore = useAppStore()

  // const payslipStore = usePayslipStore()
  const accountStore = useAccountStore()
  const dailyTimeStore = useDailyTimeStore()
  const periodStore = usePeriodStore()
  const tableStore = useTableStore()
  const scheduleStore = useScheduleStore()
  const costStore = useCostStore()
  const loanStore = useLoanStore()
  const settingsStore = useSettingsStore()


  // force load SS, PH, BIR, Holiday tables
  tableStore.init()

  const my = useUtil()

  // new Payslip is selected
  watch(selectedPayslip, async (newPaylip: Payslip, oldPaylsip: Payslip) => {
    await transactStore.getPayslipTransactions(selectedPayslip.value)

    // Closed periods are frozen: view the stored amounts, never regenerate
    // or recalculate (mirrors the recalcPeriodPayslips guard)
    const period = periodStore.periods.find(
      (item: Period) => item.uuid == selectedPayslip.value.period_uuid
    )
    if (period && period.status != PERIOD_STATUS.Open) return

    // Refresh the payslip from the current Employee when it has gone stale
    // (dirty, a snapshot field differs, or a daily-time row's schedule differs
    // from the employee's ShiftCode). Also push the ShiftCode down to the
    // daily-time rows, since calcPay reads the schedule per daily time, not from
    // the payslip header. The resynced snapshot is persisted by calcPay's final
    // updatePayslip.
    const slip = selectedPayslip.value
    const employee = employeeStore.employees.find((e: Employee) => e.uuid == slip.employee_uuid)
    if (employee != undefined) {
      const resynced = resyncPayslipFromEmployee(slip, employee)
      const SNAP_KEYS = ['salary_rate', 'salary_scheme', 'salary_calculation', 'pay_cycle',
        'days_off', 'schedule_uuid', 'cost_uuid', 'department_uuid', 'position_uuid',
        'company_uuid', 'fullname'] as const
      const headerStale = SNAP_KEYS.some((k) => (resynced as any)[k] !== (slip as any)[k])
      const dtStale = dailyTimeStore.dailyTimes.some(
        (dt) => dt?.payslip_uuid == slip.uuid && dt.schedule_uuid != employee.schedule_uuid
      )
      if (slip.dirty || headerStale || dtStale) {
        if (dtStale) await dailyTimeStore.setPayslipSchedule(slip.uuid, employee.schedule_uuid)
        // selectedPayslip.value is the same object reference as its payslips[]
        // row (Oruga v-model:selected), so mutating in place updates both
        // WITHOUT re-triggering this watch (the ref watch is shallow, not deep).
        Object.assign(selectedPayslip.value, resynced)
      }
    }

    if (transactStore.payslipTransactions.filter(t => t != undefined && t.payslip_uuid == selectedPayslip.value.uuid).length === 0) {
      await transactStore.generateTransactions(selectedPayslip.value)
      await transactStore.getPayslipTransactions(selectedPayslip.value)
    }
    await tableStore.init()
    calcPay(selectedPayslip.value)
  })

  async function generatePayslips(payload: Period) {
    if (
      payslips
        .filter((item: Payslip) => item != undefined)
        .filter((item: Payslip) => item.period_uuid == payload.uuid).length == 0
    ) {
      // calculate Holidays
      const period_uuid = payload.uuid
      let idx = 0

      const eligibleEmployees = employeeStore.employees.filter((item: Employee) => item.pay_cycle == payload.pay_cycle)
      for (const employee of eligibleEmployees) {
        const slip = {} as Payslip

        slip.employee_uuid = employee.uuid
        slip.employee_id = employee.employee_id
        slip.fullname =
          employee.lastname + ', ' + employee.firstname + ' ' + my.middleInitial(employee.middlename)
        slip.period_uuid = period_uuid

        slip.salary_rate = employee.salary_rate
        slip.salary_scheme = employee.salary_scheme || SALARY_SCHEME.Daily
        slip.salary_calculation = employee.salary_calculation || SALARY_SCHEME.Daily
        slip.pay_cycle = employee.pay_cycle

        slip.days_off = employee.days_off

        slip.company_uuid = employee.company_uuid
        slip.department_uuid = employee.department_uuid
        slip.position_uuid = employee.position_uuid
        slip.cost_uuid = employee.cost_uuid
        slip.schedule_uuid = employee.schedule_uuid

        slip.total_debit = 0.0
        slip.total_credit = 0.0
        slip.net_pay = 0.0
        slip.employee = employee
        slip.dirty = true
        await addPayslip(slip)
        idx += 1
      }
    }
  }

  // Strip frontend-only fields that the DB schema doesn't have
  function toApiPayload(payload: Payslip) {
    const { employee, ...rest } = payload as any
    return rest
  }

  async function addPayslip(payload: Payslip) {
    const response = await api.post('/payslips.json', toApiPayload(payload))

    if (response.status == 200) {
      payslips.push(response.data)
      // create the payslip details
      await transactStore.generateTransactions(response.data)
    } else {
    }
  }

  async function getPeriodPayslips(payload: Period) {
    const response = await api.get('/payslips/period_uuid/' + payload.uuid)

    if (response.status == 200) {
      payslips.splice(0)
      Object.assign(payslips, response.data.data)
    } else {
    }
  }

  async function dirtyPayslip(uuid: string) {
    const idx = payslips.findIndex((payslip: Payslip) => payslip.uuid == uuid)

    // A view that never loaded payslips (TransactView is one) still dirties them
    // after an edit. findIndex returns -1 there, and splice(-1, 1, row) would
    // replace the LAST payslip with a bogus `{dirty:true}` object, so only touch
    // the local copy when we actually hold the row. The PUT below is what counts.
    if (idx >= 0) payslips.splice(idx, 1, { ...payslips[idx], dirty: true })
    // persist so the flag survives reloads and other sessions see it
    await api.put('/payslips/' + uuid, { dirty: true })
  }

  // refresh the generation-time snapshot fields from the employee record
  function resyncPayslipFromEmployee(payslip: Payslip, employee: Employee) {
    return {
      ...payslip,
      salary_rate: employee.salary_rate,
      salary_scheme: employee.salary_scheme || SALARY_SCHEME.Daily,
      salary_calculation: employee.salary_calculation || SALARY_SCHEME.Daily,
      pay_cycle: employee.pay_cycle,
      days_off: employee.days_off,
      schedule_uuid: employee.schedule_uuid,
      cost_uuid: employee.cost_uuid,
      department_uuid: employee.department_uuid,
      position_uuid: employee.position_uuid,
      company_uuid: employee.company_uuid,
      fullname: employee.lastname + ', ' + employee.firstname + ' ' + my.middleInitial(employee.middlename)
    } as Payslip
  }

  // While periods are Open, mark payslips affected by an employee/cost/table
  // change dirty (persisted); an employee match also resyncs the snapshot
  // fields. Payslips of the currently selected period recalculate immediately
  // (App.vue keeps its daily times and transactions loaded); other Open
  // periods recalc when next viewed via recalcPeriodPayslips.
  async function dirtyAffectedPayslips(
    match: { employee_uuid?: string; cost_uuid?: string; schedule_uuid?: string } = {},
    recalcNow: boolean = true
  ) {
    const openPeriods = periodStore.periods.filter(
      (period: any) => period.status == PERIOD_STATUS.Open && period.generated_payslips
    )
    const employee = match.employee_uuid
      ? employeeStore.employees.find((item: Employee) => item.uuid == match.employee_uuid)
      : undefined

    for (const period of openPeriods) {
      const response = await api.get('/payslips/period_uuid/' + period.uuid)
      if (response.status != 200) continue

      const affected = (response.data.data as Payslip[]).filter(
        (payslip: Payslip) =>
          (!match.employee_uuid || payslip.employee_uuid == match.employee_uuid) &&
          (!match.cost_uuid || payslip.cost_uuid == match.cost_uuid) &&
          (!match.schedule_uuid || payslip.schedule_uuid == match.schedule_uuid)
      )

      for (const affected_ of affected) {
        let payslip = { ...affected_, dirty: true } as Payslip
        if (employee != undefined) {
          payslip = { ...resyncPayslipFromEmployee(payslip, employee), dirty: true }
        }
        await api.put('/payslips/' + payslip.uuid, toApiPayload(payslip))

        const idx = payslips.findIndex((item: Payslip) => item.uuid == payslip.uuid)
        if (idx >= 0) payslips.splice(idx, 1, payslip)

        if (recalcNow && period.uuid == appStore.thisPeriod?.uuid) {
          await calcPay(payslip)
        }
      }
    }
  }

  async function updatePayslip(payload: Payslip) {
    const response = await api.put('/payslips/' + payload.uuid, toApiPayload(payload))
    if (response.status == 200) {
      const idx = payslips.findIndex((item: Payslip) => item.uuid == payload.uuid)
      const data = response.data

      Object.assign(payslips[idx], data)
    } else {
    }
  }

  async function calcPay(payslip: Payslip) {
    thisLogHours.value = []

    const period = await periodStore.periods.find(
      (period: Period) => period.uuid == payslip.period_uuid
    ) as Period

    const employee = await employeeStore.employees.find(
      (employee: Employee) => employee.uuid == payslip.employee_uuid
    )

    // STEP #1 -calculate basic salary, absences, underfime, overtime, night premium
    // const hoursInDay = 9
    // const overTimeFactor = 1.3
    // const nightPremiumFactor = 0.1

    // let days_present = 0
    // let days_leave = 0
    // let days_holiday = 0
    // let days_absent = 0

    let basic_salary = 0
    let net_salary = 0
    const absences = 0
    let overtime = 0
    let night_premium = 0

    let ss_amount = 0
    let ph_amount = 0
    let pf_amount = 0
    let wtax_amount = 0

    // costcenter related earnings
    let sil = 0
    let thirtenth_month = 0
    let uniform_allowance = 0
    let supervisor_allowance = 0

    // initial paySetting here
    let _daysInYear = appStore.paySetting.daysInYear
    let _daysInYear13 = 0.00
    const _hoursInDay = appStore.paySetting.hoursInDay
    const _halfSaturday = appStore.paySetting.halfSaturday

    // in minutes
    const _undertimeAllowance = appStore.paySetting.undertimeAllowance
    const _minimumOvertime = appStore.paySetting.minimumOvertime

    // Overtime Factor from the Costs table (e.g. 1.25); 0/unset falls back
    // to the global payFactor.OT
    let _overtimeFactor = appStore.payFactor.OT

    let account = {} as Account
    let transact = {} as Transact


    /** EARNINGS AND DEDUCTIONS CALCULATED PER PAYSLIP */
    // if cost center is set, calc SIL, Supervisory Allowance and Night Diff

    // the payslip-level cost center is the fallback used for the daily allowance
    // accrual when a daily time doesn't carry its own cost distribution
    const payslipCostCenter = costStore.costCenters.find(
      (costcenter: CostCenter) => (costcenter.uuid == payslip.cost_uuid)
    )

    // start of costing
    if (payslip.cost_uuid != undefined) {

      const costcenter_ = payslipCostCenter

      if (costcenter_ != undefined) {
        uniform_allowance = (costcenter_.uniform_allowance || 0) / 2
        // SIL and 13th month now accumulate per day in the day loop, using each
        // day's own cost center (cost distribution can change per day).
        // Supervisor allowance is a monthly figure. Fixed method splits it over
        // the semi-monthly payslip (÷2, like uniform_allowance above); Daily
        // method instead accrues per day in the day loop below (0 here).
        supervisor_allowance = costcenter_.supervisor_allowance_calc === SUPERVISOR_ALLOWANCE_CALC.Daily
          ? 0
          : (costcenter_.supervisor_allowance ?? 0) / 2
        if (Number(costcenter_.overtime_factor) > 0) {
          _overtimeFactor = Number(costcenter_.overtime_factor)
        }
      }
    }

    //end of costing

    /** EARNINGS AND DEDUCTIONS CALCULATED DAILY  */
    dailyTimeStore.dailyTimes
      .filter((dailytime: DailyTime) => dailytime.payslip_uuid == payslip.uuid)
      .forEach((dt: DailyTime) => {


        /* EXTEND SETTINGS FROM COST CENTER */
        // check if cost center is set and override settings
        const costcenter = costStore.costCenters.find(
          (costcenter: any) => (costcenter.uuid == dt.cost_uuid)
        )

        // the daily time's own cost center can override the payslip-level
        // Overtime Factor
        let dayOvertimeFactor = _overtimeFactor

        if (costcenter !== undefined) {
          _daysInYear = costcenter.days_in_year ?? _daysInYear
          // hours_in_day is duty hours for contract billing, NOT the pay
          // divisor: the daily rate always covers paySetting.hoursInDay (8),
          // so HW beyond 8 pays more than one daily rate
          _daysInYear13 = costcenter.days_in_year_13 ?? _daysInYear13
          if (Number(costcenter.overtime_factor) > 0) {
            dayOvertimeFactor = Number(costcenter.overtime_factor)
          }
        }

        const schedule = scheduleStore.schedules.find(
          (schedule: Schedule) => (schedule.uuid == dt.schedule_uuid)
        )

        // const logHours = my.logHours(dt.logs as string, schedule)

        // CHECK CLIENT HERE !!!
        const logHours = my.logHours(dt.logs, schedule, 30, 15, dt.early_in, dt.late_out, dt.late_break, dt.early_resume)

        thisLogHours.value.push(logHours)


        // default factors
        let payFactor = 1.0
        // let dayFactor = 1.0

        // override day Factor is half day Saturdays

        if (_halfSaturday) {
          if (dayjs(dt.date_, 'YYYYMMDD').day() == 6) {
            // dayFactor = 0.5
          } else {
            // add one hour per week day
            // dayFactor = 1 + (1 / appStore.paySetting.hoursInDay)
          }

        }
        // convert Salary Rate to Daily
        let salary_rate = payslip.salary_scheme == SALARY_SCHEME.Monthly ? payslip.salary_rate * 12 / _daysInYear
          : payslip.salary_scheme == SALARY_SCHEME.Hourly ? payslip.salary_rate * _hoursInDay
            : payslip.salary_rate

        // The daily rate is pulled from THIS day's own cost center (cost
        // distribution can change per day), falling back to the payslip-level
        // cost center, then to the employee's own salary rate when no cost
        // center carries a daily_rate. When it comes from the Costs table the
        // rate is grossed up by days_in_year / 365 (e.g. 540 × 395 / 365) so
        // the base daily_rate spreads the paid non-working days over the year.
        const rateCC = costcenter ?? payslipCostCenter
        if (rateCC !== undefined && Number(rateCC.daily_rate) > 0) {
          const diyCC = Number(rateCC.days_in_year)
          salary_rate = Number(rateCC.daily_rate) * (diyCC > 0 ? diyCC : 365) / 365
        }


        // whether this day earns basic salary — drives the daily allowance
        // accrual below (WorkingDay with hours, or any leave / holiday day)
        let earnsBasic = false

        if (dt.day_type == DAY_TYPE.WorkingDay) {
          // days_present += dayFactor
          if (logHours[0] > 0) {
            basic_salary += logHours[0] * (salary_rate / _hoursInDay) * payFactor
            earnsBasic = true
          }
        } else if (Object.values(LEAVE_TYPE).includes(dt.day_type)) {
          // days_leave += dayFactor
          basic_salary += logHours[0] * (salary_rate / _hoursInDay) * payFactor
          earnsBasic = true
        } else if (Object.values(HOLIDAY_TYPE).includes(dt.day_type)) {
          // days_holiday += dayFactor
          if (!schedule?.without_holidays) {
            payFactor = appStore.payFactor[dt.day_type]
          }
          basic_salary += logHours[0] * (salary_rate / _hoursInDay) * payFactor
          earnsBasic = true
        }

        // Accrue cost-center allowances daily, using THIS day's own cost center
        // (falling back to the payslip-level cost center when the day carries no
        // cost distribution). SIL and 13th month are the cost table's monthly
        // figures divided by working-days-per-month (days_in_year / 12), so they
        // accumulate to the right total as the cost distribution changes across
        // the period. Supervisor allowance accrues here only when method is Daily.
        const accrualCC = costcenter ?? payslipCostCenter
        if (earnsBasic && accrualCC !== undefined) {
          const _daysInYearCC = Number(accrualCC.days_in_year)
          const dailyRate = Number(accrualCC.daily_rate)
          if (_daysInYearCC > 0 && dailyRate > 0) {
            sil += dailyRate * 5 / 365
            const diy = Number(accrualCC.days_in_year_13) > 0
              ? Number(accrualCC.days_in_year_13)
              : _daysInYearCC
            thirtenth_month += dailyRate * diy / (12 * 365)
            if (accrualCC.supervisor_allowance_calc === SUPERVISOR_ALLOWANCE_CALC.Daily) {
              supervisor_allowance += (Number(accrualCC.supervisor_allowance) || 0) * 12 / 365
            }
          }
        }


        // this is for undertime == no need to deduct salary is already net
        // if (logHours[1] > 0 && logHours[1] >= (_undertimeAllowance / 60)) {
        //   absences +=
        //     ((salary_rate * payFactor) / _hoursInDay) * logHours[1]
        // }

        // overtime — paid at the cost center's Overtime Factor (e.g. 1.25)
        if (logHours[2] > 0 && logHours[2] >= (_minimumOvertime / 60)) {

          overtime +=
            ((salary_rate) / _hoursInDay) *
            logHours[2] *
            dayOvertimeFactor

        }

        // night premium — when the day is costed, the cost center's shift
        // scales the differential (day → 0, night → 1, rotation → 1/3). Without
        // a cost center it stays fully driven by the logged 22:00–06:00 hours.
        if (logHours[3] > 0) {
          const npCC = costcenter ?? payslipCostCenter
          const shiftFactor = npCC !== undefined ? my.nightShiftFactor(npCC.shift) : 1
          night_premium +=
            ((salary_rate * payFactor) / _hoursInDay) *
            logHours[3] *
            appStore.payFactor.NP *
            shiftFactor
        }
      })


    // write the cost-center allowances once, from their final values: SIL and
    // 13th month accumulated across the day loop; uniform and (Fixed) supervisor
    // allowance set once from the payslip-level cost center in the header block.
    if (payslip.cost_uuid != undefined) {
      // SIL
      account = accountStore.accounts.find(
        (item: Account) => item.account_number == PAY_ACCOUNT.SIL
      ) as Account
      transact = transactStore.payslipTransactions.find(
        (item: Transact) => item.payslip_uuid == payslip.uuid && item.account_uuid == account?.uuid
      ) || {} as Transact
      transactStore.updateTransaction({ ...transact, amount: sil } as Transact)

      // Thirteenth Month
      account = accountStore.accounts.find(
        (item: Account) => item.account_number == PAY_ACCOUNT.ThirteenthMonth
      ) as Account
      transact = transactStore.payslipTransactions.find(
        (item: Transact) => item.payslip_uuid == payslip.uuid && item.account_uuid == account?.uuid
      ) || {} as Transact
      transactStore.updateTransaction({ ...transact, amount: thirtenth_month } as Transact)

      // Uniform Allowance
      account = accountStore.accounts.find(
        (item: Account) => item.account_number == PAY_ACCOUNT.UniformAllowance
      ) as Account
      transact = transactStore.payslipTransactions.find(
        (item: Transact) => item.payslip_uuid == payslip.uuid && item.account_uuid == account?.uuid
      ) || {} as Transact
      transactStore.updateTransaction({ ...transact, amount: uniform_allowance } as Transact)

      // Supervisor Allowance
      account = accountStore.accounts.find(
        (item: Account) => item.account_number == PAY_ACCOUNT.SupervisorAlllowance
      ) as Account
      transact = transactStore.payslipTransactions.find(
        (item: Transact) => item.payslip_uuid == payslip.uuid && item.account_uuid == account?.uuid
      ) || {} as Transact
      transactStore.updateTransaction({ ...transact, amount: supervisor_allowance } as Transact)
    }


    // update pay transaction
    account = accountStore.accounts.find(
      (item: Account) => item.account_number == PAY_ACCOUNT.BasicSalary
    ) as Account

    transact = transactStore.payslipTransactions.find(
      (item: Transact) => item.payslip_uuid == payslip.uuid && item.account_uuid == account?.uuid
    ) || {} as Transact

    transactStore.updateTransaction({ ...transact, amount: basic_salary } as Transact)

    account = accountStore.accounts.find(
      (item: Account) => item.account_number == PAY_ACCOUNT.Absences
    ) as Account

    transactStore.updateTransaction({
      ...transactStore.payslipTransactions.find(
        (transact: Transact) =>
          transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
      ),
      amount: absences
    } as Transact)

    account = accountStore.accounts.find(
      (item: Account) => item.account_number == PAY_ACCOUNT.Overtime
    ) as Account
    transactStore.updateTransaction({
      ...transactStore.payslipTransactions.find(
        (transact: Transact) =>
          transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
      ),
      amount: overtime
    } as Transact)

    account = accountStore.accounts.find(
      (item: Account) => item.account_number == PAY_ACCOUNT.NightPremium
    ) as Account
    transactStore.updateTransaction({
      ...transactStore.payslipTransactions.find(
        (transact: Transact) =>
          transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
      ),
      amount: night_premium
    } as Transact)

    // STEP #2 - calculate SS, PhilHealth and Pagibig
    const govt_calc: string = settingsStore.govtCalc
    if (govt_calc == 'basic salary') {
      net_salary = basic_salary // For Tilt: no UT / no OT
    } else {
      net_salary = basic_salary + overtime - absences
    }


    // MTD net_salary and periods_left
    let mtd_net_salary = 0;
    let mtd_taxable_salary = 0;
    if (Object.keys(employeeStore.employeeXTD).includes('mtd')) {
      const mtdSum = (account_number: string) =>
        employeeStore.employeeXTD['mtd']
          .filter((item: any) => item.account_number == account_number)
          .reduce((sum: number, item: any) => sum + item.amount, 0)

      mtd_net_salary = mtdSum(PAY_ACCOUNT.BasicSalary) + mtdSum(PAY_ACCOUNT.Overtime) - mtdSum(PAY_ACCOUNT.Absences)

      mtd_taxable_salary = mtd_net_salary
        - mtdSum(PAY_ACCOUNT.SsPremium)
        - mtdSum(PAY_ACCOUNT.PhilHealth)
        - mtdSum(PAY_ACCOUNT.Pagibig)
        // cash paid for unused leave beyond the tax-exempt days is taxable income
        + mtdSum(PAY_ACCOUNT.LeaveConversionTaxable)
    }

    let mtd_periods_left = 1;
    if (Object.keys(employeeStore.employeeXTD).includes('month_periods_left')) {
      mtd_periods_left = employeeStore.employeeXTD.month_periods_left || 1
    }


    // SS
    if (true || employee?.sss_number != '') {
      account = accountStore.accounts.find(
        (item: Account) => item.account_number == PAY_ACCOUNT.SsPremium
      ) as Account

      // mtd ss
      let mtd_ss = 0
      if (Object.keys(employeeStore.employeeXTD).includes('mtd')) {
        mtd_ss = employeeStore.employeeXTD['mtd'].filter((item: any) => item.account_number == PAY_ACCOUNT.SsPremium).reduce((sum: number, item: any) => sum + item.amount, 0)
      }

      const SS = tableStore.SS.filter((r: any) => r.From !== '').find(
        (range: any) =>
          parseFloat(range.From) < (net_salary * mtd_periods_left + mtd_net_salary) &&
          (net_salary * mtd_periods_left + mtd_net_salary) <= parseFloat(range.To)
      )

      // transact = transactStore.transactions.find(
      //   (transact) => transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
      // )
      if (SS != undefined) {
        ss_amount = (my.parseMoney(SS.EE_SS) - mtd_ss) / mtd_periods_left
      }

      transactStore.updateTransaction({
        ...transactStore.payslipTransactions.find(
          (transact: Transact) =>
            transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
        ),
        amount: ss_amount
      } as Transact)
    }

    // PhilHealth
    if (true || employee?.philhealth_number != '') {

      let mtd_ph = 0
      if (Object.keys(employeeStore.employeeXTD).includes('mtd')) {
        mtd_ph = employeeStore.employeeXTD['mtd'].filter((item: any) => item.account_number == PAY_ACCOUNT.PhilHealth).reduce((sum: number, item: any) => sum + item.amount, 0)
      }


      const PH = tableStore.PH.sort((item: any) => item.From).find(
        (range: any) =>
          parseFloat(range.From.replace(',', '')) < (net_salary * mtd_periods_left + mtd_net_salary) &&
          (net_salary * mtd_periods_left + mtd_net_salary) <= parseFloat(range.To.replace(',', ''))
      )

      account = accountStore.accounts.find(
        (item: Account) => item.account_number == PAY_ACCOUNT.PhilHealth
      ) as Account
      // transact = transactStore.transactions.find(
      //   (transact) => transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
      // )
      if (PH != undefined) {
        ph_amount = (((net_salary * mtd_periods_left + mtd_net_salary) * my.parseMoney(PH.Rate) / 2) / 100 - mtd_ph) / mtd_periods_left
      }

      transactStore.updateTransaction({
        ...transactStore.payslipTransactions.find(
          (transact: Transact) =>
            transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
        ),
        amount: ph_amount
      } as Transact)
    }

    // Pagibig
    if (true || employee?.pagibig_number != '') {
      let mtd_pf = 0
      if (Object.keys(employeeStore.employeeXTD).includes('mtd')) {
        mtd_pf = employeeStore.employeeXTD['mtd'].filter((item: any) => item.account_number == PAY_ACCOUNT.Pagibig).reduce((sum: number, item: any) => sum + item.amount, 0)
      }
      const PF = tableStore.PF.sort((item: any) => item.From).find(
        (range: any) =>
          parseFloat(range.From.replace(',', '')) < (net_salary * mtd_periods_left + mtd_net_salary) &&
          (net_salary * mtd_periods_left + mtd_net_salary) <= parseFloat(range.To.replace(',', ''))
      )

      account = accountStore.accounts.find(
        (item: Account) => item.account_number == PAY_ACCOUNT.Pagibig
      ) as Account
      // transact = transactStore.transactions.find(
      //   (transact) => transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
      // )
      if (PF != undefined) {
        // HDMF Circular 460: the rate applies to the monthly compensation only up to the
        // table's Cap (the fund salary ceiling, ₱10,000), so the share tops out at ₱200
        const projected = net_salary * mtd_periods_left + mtd_net_salary
        const fund_salary = Math.min(projected, my.parseMoney(PF.Cap) || projected)
        pf_amount = ((fund_salary * my.parseMoney(PF.EE) / 100) - mtd_pf) / mtd_periods_left
      }

      transactStore.updateTransaction({
        ...transactStore.payslipTransactions.find(
          (transact: Transact) =>
            transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
        ),
        amount: pf_amount
      } as Transact)
    }

    // Withholding Tax
    // const taxable_salary = net_salary - ss_amount - ph_amount - pf_amount
    const leaveTaxableAccount = accountStore.accounts.find(
      (a: Account) => a.account_number == PAY_ACCOUNT.LeaveConversionTaxable
    )
    const leave_conversion_taxable = !leaveTaxableAccount ? 0 : transactStore.payslipTransactions
      .filter((item: Transact) => item.payslip_uuid == payslip.uuid && item.account_uuid == leaveTaxableAccount.uuid)
      .reduce((sum: number, item: Transact) => sum + (item.amount * 1 || 0), 0)
    const taxable_salary = basic_salary + overtime - ss_amount - ph_amount - pf_amount + leave_conversion_taxable

    if (true || employee?.tax_number != '') {
      let mtd_wtax = 0
      if (Object.keys(employeeStore.employeeXTD).includes('mtd')) {
        mtd_wtax = employeeStore.employeeXTD['mtd'].filter((item: any) => item.account_number == PAY_ACCOUNT.WithholdingTax).reduce((sum: number, item: any) => sum + item.amount, 0)
      }
      const BIR = tableStore.BIR.sort((item: any) => item.From)
        .filter((item: any) => item['Payroll Frequency'].toLowerCase() == payslip.pay_cycle.toLowerCase())
        .find(
          (range: any) =>
            my.parseMoney(range.From) < (taxable_salary + mtd_taxable_salary) && (taxable_salary + mtd_taxable_salary) <= my.parseMoney(range.To)
        )

      account = accountStore.accounts.find(
        (item: Account) => item.account_number == PAY_ACCOUNT.WithholdingTax
      ) as Account
      // transact = transactStore.transactions.find(
      //   (transact) => transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
      // )


      if (BIR != undefined) {
        wtax_amount =
          ((my.parseMoney(BIR['Fix Tax']) +
            (((taxable_salary + mtd_taxable_salary) - my.parseMoney(BIR.From)) * my.parseMoney(BIR['Excess Rate'])) / 100) - mtd_wtax) / mtd_periods_left
      }

      transactStore.updateTransaction({
        ...transactStore.payslipTransactions.find(
          (transact: Transact) =>
            transact.payslip_uuid == payslip.uuid && transact.account_uuid == account?.uuid
        ),
        amount: wtax_amount
      } as Transact)
    }

    // Withheld earnings
    //
    // An earning account linked to a deduction account (account.withheld_account_uuid)
    // is mirrored 1:1 into that deduction, so the earning prints on the payslip and
    // nets to zero. Runs after every earning write above and before the totals.
    for (const { earning, withheld } of accountStore.withheldPairs) {
      const earningTx = transactStore.payslipTransactions.find(
        (item: Transact) => item?.payslip_uuid == payslip.uuid && item.account_uuid == earning.uuid
      )
      const withheldTx = transactStore.payslipTransactions.find(
        (item: Transact) => item?.payslip_uuid == payslip.uuid && item.account_uuid == withheld.uuid
      )
      if (!earningTx || !withheldTx) continue
      const amount =
        transactStore.writtenAmounts.get(earningTx.uuid as string) ?? (earningTx.amount * 1 || 0)
      await transactStore.updateTransaction({ ...withheldTx, amount } as Transact)
    }

    // Loans
    //
    // Awaited, unlike the government blocks above, because the totals below run
    // in a nextTick that reads payslipTransactions: a loan collection that has
    // not landed yet would be left out of total_credit and net_pay.
    await loanStore.postLoanDeductions(payslip, period)

    // the writes above are not awaited one by one; wait for them all to land so
    // the totals below sum the new amounts, not the ones they replaced
    await transactStore.settled()

    // calculate total earnings, total deductions and net pay
    nextTick(() => {
      const total_earnings = transactStore.payslipTransactions
        .filter((item: Transact) => item != undefined)
        .filter(
          (transact: Transact) =>
            transact.payslip_uuid == payslip.uuid && transact.account_side == ACCOUNT_SIDE.Debit
        )
        .map((transact: Transact) => transact.amount * 1 || 0)
        .reduce((tot: number, curr: number) => (tot += curr || 0), 0)

      const total_deductions = transactStore.payslipTransactions
        .filter((item: Transact) => item != undefined)
        .filter(
          (transact: Transact) =>
            transact.payslip_uuid == payslip.uuid && transact.account_side == ACCOUNT_SIDE.Credit
        )
        .map((transact: Transact) => transact.amount * 1 || 0)
        .reduce((tot: number, curr: number) => (tot += curr || 0), 0)

      const payload = {
        ...payslip,
        total_debit: total_earnings,
        total_credit: total_deductions,
        net_pay: total_earnings - total_deductions,
        // days_present: days_present,
        // days_leave: days_leave,
        // days_holiday: days_holiday,
        // days_absent: days_absent,
        dirty: false
      }

      updatePayslip(payload)
    })
  }

  async function recalcPeriodPayslips(payload: Period) {
    // Closed periods are frozen; only Open periods recalculate
    if (payload.status != PERIOD_STATUS.Open) return

    // calcPay reads schedules, cost centers, accounts, employees, daily
    // times and transactions; at app boot these load in parallel, so wait
    // for anything still missing or dirty payslips would recalc to zero
    if (scheduleStore.schedules.length == 0) await scheduleStore.getSchedules()
    if (costStore.costCenters.length == 0) await costStore.getCostCenters()
    if (accountStore.accounts.length == 0) await accountStore.getAccounts()
    if (employeeStore.employees.length == 0) await employeeStore.getEmployees()
    if (loanStore.loans.length == 0) await loanStore.getLoans()
    if (loanStore.loanTrans.length == 0) await loanStore.getLoanTrans()
    await transactStore.getPeriodTransactions(payload)
    await dailyTimeStore.getPeriodDailyTimes(payload)

    payslips
      .filter((item: Payslip) => item != undefined)
      .filter((payslip: Payslip) => payslip.period_uuid == payload.uuid && payslip.dirty)
      .forEach((payslip: Payslip) => {
        calcPay(payslip)
      })
  }

  const periodPayslips = computed(() => (period_uuid: string) => {
    return payslips.filter((item: Payslip) => item.period_uuid == period_uuid)
  })

  const hasMissingFields = computed(() => (payslip: any) => {
    if (!payslip['schedule_uuid']
      || !payslip['department_uuid']
      || !payslip['position_uuid']
      || (!payslip['company_uuid'] && appStore.options.companyEnabled)
      || (!payslip['cost_uuid'] && appStore.options.costEnabled)

      || !payslip['salary_rate']
      || !payslip['salary_scheme']
      || !payslip['salary_calculation']
    ) {
      return true
    } else {
      return false
    }
  })

  const somePayslipsHasMissingFields = computed(() => (period_uuid: string) => {
    const somePayslips = payslips.filter((item: Payslip) => item && item.period_uuid == period_uuid).filter((payslip: Payslip) => {
      if (!payslip['schedule_uuid']
        || !payslip['department_uuid']
        || !payslip['position_uuid']
        || !payslip['company_uuid']

        || !payslip['salary_rate']
        || !payslip['salary_scheme']
        || !payslip['salary_calculation']
      ) {
        return true
      } else {
        return false
      }
    }) as Array<Payslip>


    return somePayslips.length > 0
  })


  // watch(thisPayslip, async(newPayslip: Payslip,oldPayslip: Payslip)=> {

  // transactStore.getPayslipTransactions(newPayslip).then(()=>{
  //   payslipStore.calcPay(newPayslip).then(()=>{
  //     thisPayslip.value = newPayslip
  //   })

  // })
  // })

  // Resolve the daily rate used for a given daily time record, mirroring the
  // per-day resolution in calcPay: the day's own cost center daily_rate wins,
  // then the payslip-level cost center, then the employee's salary rate
  // (scheme-converted to a daily figure). Exposed for the DTR debug column.
  function dailyRate(dt: DailyTime): number {
    const payslip = payslips.find((p: Payslip) => p.uuid == dt.payslip_uuid)

    const dayCC = costStore.costCenters.find(
      (cc: CostCenter) => cc.uuid == dt.cost_uuid
    )
    const payslipCC = payslip
      ? costStore.costCenters.find((cc: CostCenter) => cc.uuid == payslip.cost_uuid)
      : undefined

    const rateCC = dayCC ?? payslipCC
    if (rateCC !== undefined && Number(rateCC.daily_rate) > 0) {
      // Costs-table rate is grossed up by days_in_year / 365 (e.g. 540 × 395 / 365)
      const diyCC = Number(rateCC.days_in_year)
      return Number(rateCC.daily_rate) * (diyCC > 0 ? diyCC : 365) / 365
    }

    if (payslip === undefined) return 0

    let daysInYear = appStore.paySetting.daysInYear
    if (dayCC !== undefined) daysInYear = dayCC.days_in_year ?? daysInYear

    return payslip.salary_scheme == SALARY_SCHEME.Monthly
      ? payslip.salary_rate * 12 / daysInYear
      : payslip.salary_scheme == SALARY_SCHEME.Hourly
        ? payslip.salary_rate * appStore.paySetting.hoursInDay
        : payslip.salary_rate
  }

  return {
    showForm,
    payslips,
    selectedPayslip,
    thisPayslip,
    periodPayslips,

    generatePayslips,
    getPeriodPayslips,

    updatePayslip,
    calcPay,
    recalcPeriodPayslips,
    dirtyPayslip,
    dirtyAffectedPayslips,
    resyncPayslipFromEmployee,
    thisLogHours,
    dailyRate,

    hasMissingFields,
    somePayslipsHasMissingFields
  }
})
